import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { z } from "zod";

const passwordSchema = z.string().min(6, "Password must be at least 6 characters");
const emailSchema = z.string().email("Invalid email address");

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<any>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      toast({
        title: "Invalid Invitation",
        description: "No invitation token found",
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    fetchInvitation();
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const { data, error } = await supabase
        .from("member_invitations")
        .select(`
          *,
          members!inner(full_name),
          ptas!inner(name)
        `)
        .eq("token", token)
        .eq("status", "pending")
        .single();

      if (error) throw error;

      if (!data) {
        toast({
          title: "Invalid Invitation",
          description: "This invitation does not exist or has already been used",
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      if (new Date(data.expires_at) < new Date()) {
        toast({
          title: "Invitation Expired",
          description: "This invitation has expired. Please contact your PTA admin for a new invitation.",
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      setInvitation(data);
    } catch (error: any) {
      console.error("Error fetching invitation:", error);
      toast({
        title: "Error",
        description: "Failed to load invitation details",
        variant: "destructive",
      });
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setConfirmPasswordError("");

    // Validate password
    try {
      passwordSchema.parse(password);
    } catch (err) {
      if (err instanceof z.ZodError) {
        setPasswordError(err.errors[0].message);
        return;
      }
    }

    // Validate password confirmation
    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }

    setSubmitting(true);

    try {
      // Create user account
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: invitation.email,
        password: password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: invitation.members.full_name,
          }
        }
      });

      if (signUpError) throw signUpError;

      if (!signUpData.user) {
        throw new Error("Failed to create user account");
      }

      // Update member record with user_id
      const { error: updateError } = await supabase
        .from("members")
        .update({ user_id: signUpData.user.id })
        .eq("id", invitation.member_id);

      if (updateError) throw updateError;

      // Fetch member roles to add to user_roles
      const { data: memberData, error: memberFetchError } = await supabase
        .from("members")
        .select("role, custom_role_name")
        .eq("id", invitation.member_id)
        .single();

      if (memberFetchError) throw memberFetchError;

      // Parse roles from member.role field and add to user_roles
      if (memberData?.role) {
        const roleMapping: { [key: string]: string } = {
          "Chair": "chair",
          "Treasurer": "treasurer",
          "Secretary": "secretary",
          "Trustee": "trustee",
          "Catering": "catering",
          "Communications": "communications",
          "School Liaison": "school_liaison",
          "Assets": "assets",
          "Community": "community",
          "Shop": "shop",
          "Events": "events"
        };

        const roleList = memberData.role.split(",").map((r: string) => r.trim());
        const rolesToAdd = roleList
          .map((roleName: string) => roleMapping[roleName])
          .filter((role: string | undefined): role is string => role !== undefined);

        if (rolesToAdd.length > 0) {
          await supabase.from("user_roles").insert(
            rolesToAdd.map(role => ({
              user_id: signUpData.user.id,
              role: role as any
            }))
          );
        }
      }

      // Mark invitation as accepted
      const { error: inviteError } = await supabase
        .from("member_invitations")
        .update({ 
          status: "accepted",
          accepted_at: new Date().toISOString()
        })
        .eq("id", invitation.id);

      if (inviteError) throw inviteError;

      toast({
        title: "Account Created!",
        description: "Your account has been created successfully. You can now log in.",
      });

      // Sign in the user automatically
      await supabase.auth.signInWithPassword({
        email: invitation.email,
        password: password,
      });

      navigate("/dashboard");
    } catch (error: any) {
      console.error("Error accepting invitation:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create account",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Accept Invitation</CardTitle>
          <CardDescription>
            You've been invited to join <strong>{invitation.ptas.name}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAccept} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={invitation.members.full_name} disabled />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={invitation.email} disabled />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              {passwordError && (
                <p className="text-sm text-destructive">{passwordError}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
              />
              {confirmPasswordError && (
                <p className="text-sm text-destructive">{confirmPasswordError}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                "Accept Invitation & Create Account"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
